import { useCallback, useMemo, useState, type MouseEvent } from 'react'
import { DocumentEorpModal } from './DocumentEorpModal'
import {
  extractEorpDocumentId,
  isEorpDocumentLink,
  processServiceAgreementHtml,
} from '../utils/serviceAgreementHtml'
import type { ServiceAgreementHtmlProps } from '../types'
import '../styles/serviceAgreementHtml.css'

/**
 * Renders service-agreement / GDPR consent HTML with Angular parity:
 * - `{DOCUMENT_ID}` replaced
 * - links styled as links
 * - `explanation-service-aggrement` / `/eorp/:id` clicks open the EORP popup
 * - API 404 descriptions surfaced via DocumentEorpModal → showApiError
 */
export function ServiceAgreementHtml({
  html,
  documentId,
  eorpSection,
  className,
  onScroll,
  contentRef,
  emptyFallback = null,
  'data-testid': dataTestId,
}: ServiceAgreementHtmlProps) {
  const [eorpOpen, setEorpOpen] = useState(false)
  const [eorpTargetId, setEorpTargetId] = useState<number | null>(null)

  const contentHtml = useMemo(
    () => processServiceAgreementHtml(html, documentId),
    [html, documentId],
  )

  const handleContentClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const anchor = (event.target as HTMLElement | null)?.closest?.('a')
      if (!anchor) return
      const href = anchor.getAttribute('href')
      if (!isEorpDocumentLink(href)) return

      event.preventDefault()
      event.stopPropagation()

      const fromHref = extractEorpDocumentId(href)
      const fallbackId =
        documentId != null && documentId !== '' ? Number(documentId) : null
      const id = fromHref ?? (fallbackId && Number.isFinite(fallbackId) ? fallbackId : null)
      if (!id) return
      setEorpTargetId(id)
      setEorpOpen(true)
    },
    [documentId],
  )

  const closeEorp = useCallback(() => {
    setEorpOpen(false)
    setEorpTargetId(null)
  }, [])

  if (!contentHtml) {
    return emptyFallback ? <>{emptyFallback}</> : null
  }

  return (
    <>
      <div
        ref={contentRef}
        onScroll={onScroll}
        onClick={handleContentClick}
        className={['service-agreement-html', className].filter(Boolean).join(' ')}
        dangerouslySetInnerHTML={{ __html: contentHtml }}
        data-testid={dataTestId}
      />
      <DocumentEorpModal
        isOpen={eorpOpen}
        documentId={eorpTargetId}
        initialHtml={
          eorpTargetId &&
          documentId != null &&
          Number(documentId) === eorpTargetId
            ? eorpSection
            : null
        }
        onClose={closeEorp}
      />
    </>
  )
}
